import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  VentilationAutomatedModeLevel,
  VentilationAutomatedModeLevelCommands,
  VentilationAutomatedModeLevelProperties,
} from '@vi-iot/client/index';
import { Observable, Subject } from 'rxjs';
import {
  CloseEventDialog,
  VentilationResetToDefaultConfirmModalComponent,
} from '../ventilation-reset-to-default-confirm-modal/ventilation-reset-to-default-confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { InstallationFeaturesStore } from '@store';
import { Value2Pipe } from 'src/app/operational-data-common/feature-pipes/value-strategy-based/value2.pipe';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-ventilation-automated-mode-level',
  templateUrl: './ventilation-automated-mode-level.component.html',
  styleUrls: ['./ventilation-automated-mode-level.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationAutomatedModeLevelComponent implements OnInit, OnDestroy {
  minimumCheckedLevel: string;
  maximumCheckedLevel: string;
  isInvalidLevelSelection = false;

  private defaultValues = {
    minimumLevel: 'levelOne',
    maximumLevel: 'levelFour',
  };

  private featureValues: {
    minimumLevel: string;
    maximumLevel: string;
  };

  ventilationAutomatedModeLevel$: Observable<
    Feature<VentilationAutomatedModeLevelProperties, VentilationAutomatedModeLevelCommands>
  >;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private dialog: MatDialog,
    private value2Pipe: Value2Pipe,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.ventilationAutomatedModeLevel$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationAutomatedModeLevel
    );

    this.ventilationAutomatedModeLevel$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.minimumCheckedLevel =
          this.value2Pipe.transform(feature, FeaturePropertyKey.minimumLevel) ??
          this.defaultValues.minimumLevel;
        this.maximumCheckedLevel =
          this.value2Pipe.transform(feature, FeaturePropertyKey.maximumLevel) ??
          this.defaultValues.maximumLevel;
        this.featureValues = {
          minimumLevel: this.minimumCheckedLevel,
          maximumLevel: this.maximumCheckedLevel,
        };
      });
  }

  onVentilationMinimumCheckedLevelChange(event: string): void {
    this.minimumCheckedLevel = event;
    this.checkLevelSelectionValidity();
  }

  onVentilationMaximumCheckedLevelChange(event: string): void {
    this.maximumCheckedLevel = event;
    this.checkLevelSelectionValidity();
  }

  checkLevelSelectionValidity(): void {
    const minLevel = VentilationAutomatedModeLevel[this.minimumCheckedLevel];
    const maxLevel = VentilationAutomatedModeLevel[this.maximumCheckedLevel];
    this.isInvalidLevelSelection = maxLevel <= minLevel;
  }

  resetToDefault(): void {
    const dialogRef = this.dialog.open(VentilationResetToDefaultConfirmModalComponent, {
      width: '575px',
      panelClass: 'ventilation-reset-to-default-levels-confirm-dialog',
    });

    dialogRef.afterClosed().subscribe((event: CloseEventDialog) => {
      if (event.confirmed) {
        if (
          this.featureValues &&
          this.featureValues.minimumLevel === this.defaultValues.minimumLevel &&
          this.featureValues.maximumLevel === this.defaultValues.maximumLevel
        ) {
          this.minimumCheckedLevel = this.defaultValues.minimumLevel;
          this.maximumCheckedLevel = this.defaultValues.maximumLevel;
        } else {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.ventilationAutomatedModeLevel,
            null,
            null,
            null,
            FeatureCommands.setDefaultLevels
          );
        }
        this.isInvalidLevelSelection = false;
        this.cdr.markForCheck();
      }
    });
  }

  save(): void {
    if (!this.isInvalidLevelSelection) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.ventilationAutomatedModeLevel,
        [this.minimumCheckedLevel, this.maximumCheckedLevel],
        [FeaturePropertyKey.minimumLevel, FeaturePropertyKey.maximumLevel],
        [FeatureActionFields.minimumLevel, FeatureActionFields.maximumLevel],
        FeatureCommands.setLevels
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
